<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import ViewStep, { StepDraft } from '@/components/step/ViewStep.vue'
import { OperatorModuleViewTreeNode } from '@/views/organization/operator/OperatorModuleView.vue'
import CompleteProductionBatch from '@/components/productionBatch/CompleteProductionBatch.vue'
import StepChecks from '@/components/check/StepChecks.vue'
import { useRouter } from 'vue-router'
import CreateIssueDialog from '@/components/issue/CreateIssueDialog.vue'
import StepChangeBanner from '@/components/operator/StepChangeBanner.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import useCurrentProductionBatch from '@/components/operator/currentProductionBatch'
import useOperatorFontSize from '@/composables/useOperatorFontSize'
import useAuthorization from '@/composables/useAuthorization'
import { PermissionOrganization, StepChangeKind } from '@/generated/graphql'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'

const props = defineProps<{
  bopId: string
  moduleId: string
  stepId?: string
  nodes: OperatorModuleViewTreeNode[]
  preview?: boolean
  draftStepId?: string
  draft?: StepDraft
}>()
const emit = defineEmits<{
  selectStepId: [value: string | undefined]
  done: []
}>()

const { t } = useI18n()
const { hasOrgAuthz } = useAuthorization()
const { scale: fontScale } = useOperatorFontSize()
const router = useRouter()

const viableNodes = computed(() => {
  const steps: OperatorModuleViewTreeNode[] = []
  props.nodes.forEach((n) => recursiveAddIfChildless(n, steps))
  return steps
})

const changedStepIds = computed(
  () =>
    new Set(
      viableNodes.value
        .filter(
          (n) =>
            n.item.changeKind == StepChangeKind.Modified ||
            n.item.changeKind == StepChangeKind.Added,
        )
        .map((n) => n.item.step.id),
    ),
)
function recursiveAddIfChildless(
  node: OperatorModuleViewTreeNode,
  aggregator: OperatorModuleViewTreeNode[],
) {
  // Ghosts of removed steps live in the tree but are not part of the walkable flow.
  if (node.item.removed) {
    return
  }

  const children = node.children.filter((n) => !n.item.removed)
  if (children.length == 0) {
    aggregator.push(node)
    return
  }

  children.forEach((n) => recursiveAddIfChildless(n, aggregator))
}
watch(viableNodes, (v) => {
  if (props.stepId || v.length == 0) {
    return
  }

  emit('selectStepId', v[0].item.step.id)
})

const stepperModel = ref<number>(0)
// Guard: prevent markModuleViewed from firing on initial render before the user has navigated
const isInitialized = ref(false)

const hasReachedLastStep = ref(false)

watch(stepperModel, (v) => {
  emit('selectStepId', viableNodes.value[v].item.step.id)
  if (isInitialized.value && v === viableNodes.value.length - 1) {
    hasReachedLastStep.value = true
  }
})

onBeforeUnmount(() => {
  if (hasReachedLastStep.value) {
    markModuleViewed({ bopId: props.bopId, moduleId: props.moduleId })
  }
})

watch(
  () => [props.stepId, viableNodes.value],
  () => {
    const index = viableNodes.value.findIndex((s) => s.item.step.id == props.stepId)
    if (index >= 0) {
      stepperModel.value = index
    }
    // Mark as initialized after the first sync so subsequent user-driven changes are tracked
    nextTick(() => {
      isInitialized.value = true
    })
  },
  { immediate: true },
)

const isLastWindow = computed(() => stepperModel.value == viableNodes.value.length - 1)

const { mutate: markModuleViewed } = useMutation(gql`
  mutation MarkModuleViewed($bopId: ID!, $moduleId: ID!) {
    shopFloor {
      markModuleViewed(bopId: $bopId, moduleId: $moduleId)
    }
  }
`)

function next() {
  stepperModel.value = stepperModel.value + 1
}

function previous() {
  stepperModel.value = stepperModel.value - 1
}

const { currentProductionBatch } = useCurrentProductionBatch()
const createIssueDialog = ref<ComponentExposed<typeof CreateIssueDialog>>()
const createIssue = () => {
  const seed = currentProductionBatch.value
    ? {
        bopId: props.bopId,
        moduleId: props.moduleId,
        stepId: props.stepId as string,
        poNumber: currentProductionBatch.value.poNumber,
        serialNumbers: currentProductionBatch.value.serialNumbers,
      }
    : {
        bopId: props.bopId,
        moduleId: props.moduleId,
        stepId: props.stepId as string,
      }
  createIssueDialog.value?.open(seed)
}

const showDiffDialog = ref(false)
const activeDiffStepId = ref<string | null>(null)

function openDiffDialog(stepId: string) {
  activeDiffStepId.value = stepId
  showDiffDialog.value = true
}
</script>

<template>
  <v-card class="operator-module-stepper" :style="{ '--operator-font-scale': fontScale }">
    <v-card-title>
      <v-btn variant="tonal" :disabled="stepperModel == 0" @click="previous">
        <v-icon icon="arrow_backward" />
        {{ t('component.operatorModuleStepper.previousButton') }}
      </v-btn>
      <v-btn v-if="!isLastWindow" variant="tonal" color="primary" class="float-right" @click="next">
        {{ t('component.operatorModuleStepper.nextButton') }}
        <v-icon icon="arrow_forward" />
      </v-btn>
      <complete-production-batch
        v-if="isLastWindow && !preview"
        :module-id="moduleId"
        variant="tonal"
        color="primary"
        @completed="router.push({ name: 'operatorHome' })"
      />
    </v-card-title>
    <v-card-text>
      <v-window
        v-model="stepperModel"
        class="mt-3 mb-3"
        :transition="preview ? false : undefined"
        :reverse-transition="preview ? false : undefined"
      >
        <v-window-item
          v-for="(s, i) in viableNodes"
          :key="s.item.step.id"
          :value="i"
          :eager="i == stepperModel + 1 || i == stepperModel - 1"
        >
          <view-step
            :step-id="s.item.step.id"
            :bop-id="preview ? undefined : bopId"
            :graph-node-step-id="s.item.isLibraryReference ? s.item.id : undefined"
            :draft="s.item.step.id == draftStepId ? draft : undefined"
          >
            <template v-if="!preview && changedStepIds.has(s.item.step.id)" #titleActions>
              <v-btn
                variant="outlined"
                color="error"
                size="small"
                @click="openDiffDialog(s.item.step.id)"
              >
                {{ t('component.operatorModuleStepper.viewChanges') }}
              </v-btn>
            </template>
          </view-step>
          <step-checks :step-id="s.item.step.id" :disabled="preview" class="mt-10 mb-3" />
        </v-window-item>
      </v-window>
    </v-card-text>
    <v-card-actions>
      <v-btn variant="tonal" :disabled="stepperModel == 0" @click="previous">
        <v-icon icon="arrow_backward" />
        {{ t('component.operatorModuleStepper.previousButton') }}
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="hasOrgAuthz(PermissionOrganization.OrgIssueWrite) && !preview"
        variant="tonal"
        @click="createIssue"
      >
        {{ t('component.operatorModuleStepper.issueButton') }}
      </v-btn>
      <v-spacer />
      <v-btn v-if="!isLastWindow" variant="tonal" color="primary" @click="next">
        {{ t('component.operatorModuleStepper.nextButton') }}
        <v-icon icon="arrow_forward" />
      </v-btn>
      <complete-production-batch
        v-if="isLastWindow && !preview"
        :module-id="moduleId"
        variant="tonal"
        color="primary"
        @completed="router.push({ name: 'operatorHome' })"
      />
    </v-card-actions>
  </v-card>

  <div v-if="!preview" class="bottom-spacer" />

  <v-dialog v-model="showDiffDialog" max-width="1100">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ t('component.stepChangeBanner.title') }}</span>
        <v-btn icon="close" variant="text" @click="showDiffDialog = false" />
      </v-card-title>
      <v-card-text class="step-diff-dialog-body">
        <step-change-banner
          v-if="activeDiffStepId"
          :key="activeDiffStepId"
          :bop-id="bopId"
          :module-id="moduleId"
          :step-id="activeDiffStepId"
        />
      </v-card-text>
      <v-card-actions class="justify-center pb-4">
        <v-btn color="primary" variant="tonal" @click="showDiffDialog = false">
          {{ t('component.operatorModuleStepper.diffDialogDismiss') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <create-issue-dialog v-if="!preview" ref="createIssueDialog" />
</template>

<style scoped lang="scss">
.step-diff-dialog-body {
  height: 70vh;
  overflow-y: auto;
}

.bottom-spacer {
  height: 40vh;
}
</style>
